<template>
    <VJoyWrapper direction="column" align="stretch" :row-gap="panelsView ? '4' : '6'">
        <PanelOrNothing :panel-visible="panelsView">
            <VJoyWrapper no-margin direction="column" align="flex-start" row-gap="0">
                <VJoyLabel :text="t('profile.new.experience.company')" />
                <VJoyWrapper class="profile-edit-experience-form__company" no-margin direction="column" align="stretch" row-gap="2">
                    <VJoyWrapper no-margin wrap="nowrap" direction="row" column-gap="4">
                        <CompanyAutocomplete
                            :is-required="r$.company.$dirty"
                            :model-value="{company: experience.company || '', companyId: experience.companyId}"
                            :activity-areas="activityAreas"
                            :data-testid="`${dataTestid}--form-autocomplete-company`"
                            :error="r$.company.$error"
                            :disabled="isExperienceLinkedToAMaltProject"
                            @update:model-value="handleCompanyUpdate"
                        />
                        <VJoyAvatar :is-empty="true" :data-testid="`${dataTestid}--form-company-logo`">
                            <MaltImg v-if="oldCompanyLogo" fit="contain" width="46" height="46" :src="oldCompanyLogo" alt="company-logo" />
                            <img
                                v-else-if="newCompanyLogoUrl"
                                width="46"
                                height="46"
                                :src="newCompanyLogoUrl"
                                alt="company-logo"
                                style="object-fit: contain"
                            />
                            <VJoyIcon v-else size="small" name="company-placeholder" />
                        </VJoyAvatar>
                    </VJoyWrapper>
                    <VJoyFormError v-if="r$.company.$error" :errors="r$.company.$errors" no-margin />
                    <VJoyWrapper
                        v-if="isExperienceLinkedToAMaltProject"
                        :data-testid="`${dataTestid}--form-company-legend`"
                        no-margin
                        direction="row"
                        column-gap="2"
                        row-gap="0"
                    >
                        <VJoyWrapper no-margin direction="row" wrap="nowrap" column-gap="2" row-gap="1">
                            <VJoyIcon name="info-circle" color="neutral" size="xsmall" />
                            <span class="profile-edit-experience-form__company-legend">
                                {{ t('profile.new.experience.company.locked') }}
                            </span>
                        </VJoyWrapper>
                        <VJoyLink
                            class="profile-edit-experience-form__company-legend-link"
                            :href="t('profile.new.experience.company.locked.link')"
                            target="_blank"
                            :text="t('profile.new.experience.company.locked.learn.more')"
                        />
                    </VJoyWrapper>
                </VJoyWrapper>
            </VJoyWrapper>
            <VJoyWrapper direction="column" align="stretch" row-gap="2" no-margin>
                <VJoyInput
                    v-model.trim="r$.$value.title"
                    :required="true"
                    name="profile-experience-form-title"
                    :label="fields?.title?.label || t('profile.new.experience.title')"
                    :data-testid="`${dataTestid}--form-input-title`"
                    :invalid="r$.title.$error"
                />
                <VJoyFormError v-if="r$.title.$error" :errors="r$.title.$errors" no-margin />
            </VJoyWrapper>
            <VJoyWrapper direction="row" wrap="nowrap" align="flex-start" :no-margin="true" row-gap="4">
                <VJoyWrapper style="flex: 1 1 50%" direction="column" align="stretch" item-stretch :no-margin="true" row-gap="2">
                    <MaltDatePicker
                        v-model="experience.startDate"
                        view="month"
                        name="experience-start-date"
                        :input-id="`edit-experience-input-startdate--${experience.id}`"
                        :data-testid="`${dataTestid}--form-calendar-startdate`"
                        :min-date="experienceStartDateMin"
                        :max-date="experienceStartDateMax"
                        :invalid="r$.startDate.$error"
                        :label="fields?.startDate?.label || t('profile.new.experience.first.date.label.new')"
                    />
                    <VJoyFormError v-if="r$.startDate.$error" :errors="r$.startDate.$errors" no-margin />
                    <VJoyCheckbox
                        v-if="!hiddenFields?.includes('asFreelancer')"
                        :checked="experience.asFreelancer"
                        name="profile-experience-form-asFreelancer"
                        :data-testid="`${dataTestid}--form-checkbox-asFreelancer`"
                        @update:checked="(checked) => (experience.asFreelancer = checked)"
                    >
                        {{ t('profile.section.experience.label.as.freelancer') }}
                    </VJoyCheckbox>
                </VJoyWrapper>

                <VJoyWrapper style="flex: 1 1 50%" direction="column" align="stretch" :no-margin="true" row-gap="2">
                    <MaltDatePicker
                        v-model="experience.endDate"
                        view="month"
                        name="experience-end-date"
                        :input-id="`edit-experience-input-enddate--${experience.id}`"
                        :data-testid="`${dataTestid}--form-calendar-enddate`"
                        :min-date="experienceEndDateMin"
                        :max-date="experienceEndDateMax"
                        :invalid="r$.endDate.$error"
                        :disabled="!isEnabledEndDate"
                        :label="fields?.endDate?.label || t('profile.new.experience.second.date.label.new')"
                    />
                    <VJoyFormError v-if="r$.endDate.$error" :errors="r$.endDate.$errors" no-margin />
                    <VJoyCheckbox
                        v-if="!hiddenFields?.includes('currentJob')"
                        :checked="experience.currentJob"
                        :label="t('profile.new.experience.job.still.in.progress')"
                        :data-testid="`${dataTestid}--form-checkbox-currentJob`"
                        @update:checked="handleCurrentJobChecked"
                    ></VJoyCheckbox>
                </VJoyWrapper>
            </VJoyWrapper>
        </PanelOrNothing>
        <PanelOrNothing :panel-visible="panelsView">
            <Wysiwyg
                ref="wysiwygRef"
                :key="`edit-experience-wysiwyg-description--${experience.id}`"
                class="profile-edit-experience-form__wysiwyg"
                :label="fields?.description?.label || t('profile.new.experience.description')"
                :required="false"
                :placeholder="fields?.description?.placeholder || t('profile.new.experience.description.placeholder')"
                :model-value="experience.description"
                :data-testid="`${dataTestid}--form-textarea-description`"
                input-name="profile-experience-form-description"
                :invalid="r$.description.$error"
                :min-counter="true"
                :minlength="experienceDescriptionLength.min"
                :maxlength="experienceDescriptionLength.max"
                :minlength-label="
                    t('profile.section.experience.description.minLength.label', {
                        0: experienceDescriptionLength.min,
                    })
                "
                :strict-validation="true"
                @update:model-value="(value: string) => (experience.description = value)"
            />
            <div v-if="!hiddenFields?.includes('skills')" class="profile-edition__experience">
                <VJoyWrapper class="profile-edit-experience-form__skills-label-wrapper" direction="row" row-gap="2">
                    <VJoyLabel class="profile-edit-experience-form__skills-label" :optional-label="t('generic.form.optional.mention')">
                        {{ t('profile.section.experience.edit.skill.label') }}
                    </VJoyLabel>
                    <SkillsCounter
                        class="profile-edit-experience-form__skills-counter"
                        :count="experienceSkills.length"
                        :limit="experienceMaxSkills"
                    />
                </VJoyWrapper>

                <EditGlobalSkills
                    :model-value="experienceSkills"
                    :rules-global-skill-label-min-length="skillSetGlobalSkillLabel.min"
                    :rules-global-skill-label-max-length="skillSetGlobalSkillLabel.max"
                    :show-label="false"
                    :show-tag-list="true"
                    :show-occurences="true"
                    :disabled="experienceSkills.length >= experienceMaxSkills"
                    :data-testid="`${dataTestid}--form-autocomplete-skills`"
                    @update:model-value="handleEditSkillsUpdateModelValue"
                />
                <VJoyFormError
                    v-if="r$.skills.$error"
                    ref="skillsFormError"
                    :data-testid="`${dataTestid}--form-autocomplete-skills-error`"
                    class="joy-mt-3"
                >
                    {{ t('profile.section.experience.edit.tag.error.max', {0: experienceMaxSkills}) }}
                </VJoyFormError>
                <ClientOnly>
                    <SkillsSuggestions
                        v-show="!r$.skills.$error"
                        :skills="experienceSkills ?? []"
                        :description="experience.description"
                        @skill-selected="handleSkillSuggestedSelected"
                    />
                </ClientOnly>
            </div>
        </PanelOrNothing>

        <PanelOrNothing :panel-visible="panelsView">
            <div>
                <VJoyLabel :optional-label="t('generic.form.optional.mention')">
                    {{ fields?.location?.label || t('profile.new.experience.location') }}
                </VJoyLabel>
                <LocationAutocomplete
                    :append-to-element="appendToElement"
                    :disabled="false"
                    :model-value="experienceLocation"
                    :data-testid="`${dataTestid}--form-autocomplete-location`"
                    @update:model-value="handleLocationUpdate"
                />
            </div>
            <VJoySelect
                v-if="!hiddenFields?.includes('area')"
                v-model.trim="experience.area"
                :optional-label="t('generic.form.optional.mention')"
                :label="fields?.area?.label || t('profile.new.experience.area.placeholder')"
                name="experience-form-area-select"
                :data-testid="`${dataTestid}--form-select-area`"
            >
                <option :value="null"></option>
                <option v-for="[area, text] in activityAreas" :key="`experience-form-area-select--${area}`" :value="area">
                    {{ text }}
                </option>
            </VJoySelect>
        </PanelOrNothing>
    </VJoyWrapper>
</template>
<script setup lang="ts">
    import {computed, ref, toValue, useTemplateRef, watch} from 'vue';
    import type {InferRegleRoot} from '@regle/core';
    import {storeToRefs} from 'pinia';
    import {MaltDatePicker, MaltImg, Wysiwyg} from '#components';
    import {useTranslation} from '#imports';
    import {useFeatureFlag} from '@malt/feature-flags';
    import {VJoyAvatar, VJoyCheckbox, VJoyFormError, VJoyIcon, VJoyInput, VJoyLabel, VJoyLink, VJoySelect, VJoyWrapper} from '@maltjoy/core-vue';
    import {ActivityArea, type PostalAddress, type Skill} from '@api';
    import CompanyAutocomplete, {type CompanyAutocompleteModel} from '~/components/edit/companies/CompanyAutocomplete.vue';
    import EditGlobalSkills from '~/components/edit/skill-set/EditGlobalSkills.vue';
    import SkillsCounter from '~/components/edit/skills/SkillsCounter.vue';
    import LocationAutocomplete from '~/components/LocationAutocomplete.vue';
    import type {EditExperience} from '~/features/experience/types/EditExperience';
    import {useRulesStore} from '~/store/rules/rulesStore';
    import PanelOrNothing from './PanelOrNothing.vue';
    import SkillsSuggestions from './SkillsSuggestions.vue';
    import {useExperienceForm} from '../../composable/useExperienceForm';

    type FieldConfig = {
        hidden?: boolean;
        label?: string;
        placeholder?: string;
    };

    const experience = defineModel<EditExperience>({required: true});
    const props = withDefaults(
        defineProps<{
            appendToElement?: string;
            newExperience?: boolean;
            fields?: Partial<Record<keyof EditExperience, FieldConfig>>;
            panelsView?: boolean;
            validation?: InferRegleRoot<typeof useExperienceForm>;
        }>(),
        {
            appendToElement: '.profile-id-or-alias',
            newExperience: false,
            panelsView: false,
            validation: undefined,
            fields: undefined,
        },
    );

    const {t} = useTranslation();

    const rulesStore = useRulesStore();
    const {experienceDescriptionLength, skillSetGlobalSkillLabel, experienceMaxSkills} = storeToRefs(rulesStore);

    const hiddenFields = computed(() =>
        Object.entries(props.fields || {})
            .filter(([_, config]) => config.hidden)
            .map(([key]) => key as keyof EditExperience),
    );
    const {
        r$: localR$,
        experienceStartDateMin,
        experienceStartDateMax,
        experienceEndDateMin,
        experienceEndDateMax,
    } = useExperienceForm({experience, hiddenFields, isLockedToMaltProject: !!experience.value.missionId});

    const r$ = computed(() => toValue(props.validation) ?? localR$);

    const isNewCompanyLogoEnabled = useFeatureFlag('new-company-logo');
    const dataTestid = computed(() => `profile-edition-experience-form-item--${props.newExperience ? 'add' : experience.value.id}`);
    const isExperienceLinkedToAMaltProject = computed(() => !!experience.value.missionId);
    const oldCompanyLogo = computed(() =>
        experience.value.logo?.baseUrl && experience.value.logo?.name ? `${experience.value.logo.baseUrl}/${experience.value.logo.name}` : undefined,
    );
    const newCompanyLogoUrl = computed(() =>
        !oldCompanyLogo.value && isNewCompanyLogoEnabled.value ? (experience.value.logoUrl ?? undefined) : undefined,
    );
    const isEnabledEndDate = computed(() => !experience.value.currentJob);
    const experienceLocation = computed(() => experience.value.location);
    const experienceSkills = ref<Skill[]>([]);

    watch(
        experience.value,
        () => {
            experienceSkills.value = copyExperienceSkills(experience.value.skills);
        },
        {immediate: true},
    );

    const activityAreas: Map<ActivityArea, string> = new Map(
        Object.values(ActivityArea)
            .filter((value) => isNaN(Number(value)))
            .map((activityArea) => [activityArea, t(`company.activity.area.${activityArea}`)] as const)
            .sort((a, b) => a[1].localeCompare(b[1])),
    );

    function handleCompanyUpdate(value: CompanyAutocompleteModel) {
        experience.value.company = value.company;
        experience.value.companyId = value.companyId;
        if (value.area) {
            experience.value.area = value.area;
        }
        experience.value.logo = value.logo?.baseUrl && value.logo?.name ? value.logo : undefined;
        experience.value.logoUrl = isNewCompanyLogoEnabled.value ? value.logoUrl : undefined;
    }

    function handleCurrentJobChecked(checked: boolean) {
        experience.value.currentJob = checked;
        if (checked) {
            experience.value.endDate = undefined;
        }
    }

    function handleLocationUpdate(location?: PostalAddress): void {
        experience.value.location = location;
    }

    const skillsFormError = useTemplateRef('skillsFormError');

    function handleEditSkillsUpdateModelValue(modelValue: Skill[]) {
        experience.value.skills = modelValue;
        setTimeout(() => {
            skillsFormError.value?.$el.scrollIntoView({behavior: 'smooth'});
        }, 100);
    }

    function handleSkillSuggestedSelected(suggestedSkill: Skill) {
        experience.value.skills = [...(experience.value.skills ?? []), suggestedSkill];
        setTimeout(() => {
            skillsFormError.value?.$el.scrollIntoView({behavior: 'smooth'});
        }, 100);
    }

    function copyExperienceSkills(skills: Skill[] | undefined) {
        return [...(skills?.map((skill) => ({...skill})) ?? [])];
    }
</script>
<style scoped lang="scss">
    .profile-edit-experience-form {
        &__wysiwyg {
            :deep(.wysiwyg-editor__helpers) {
                margin-bottom: 0;
            }
        }

        &__company {
            width: 100%;

            :deep(.joy-avatar__wrapper--medium) {
                --avatar-size: 48px;
            }

            :deep(.joy-avatar--empty) {
                border-color: var(--joy-color-neutral-30);
            }
        }

        &__company-legend {
            color: var(--joy-color-neutral-40);
            font-size: var(--joy-font-size-primary-300);
        }

        &__company-legend-link {
            font-size: var(--joy-font-size-primary-300);
        }

        &__calendar-fallback {
            width: 100%;
            margin-top: var(--joy-core-spacing-4);
        }

        &__calendar-label {
            min-height: var(--joy-core-spacing-5);
        }

        &__skills-label-wrapper {
            margin-bottom: var(--joy-core-spacing-2) !important;
            margin-right: var(--joy-core-spacing-2);
        }

        &__skills-counter {
            flex-wrap: nowrap !important;
            white-space: nowrap;
        }

        &__skills-label {
            margin-bottom: 0 !important;
        }
    }
</style>
